import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

type SubmitActionsProps = {
  canSubmit: boolean;
  isSubmitting: boolean;
  submitError: string | null;
  onCancel: () => void;
  lockedEpisodeId?: string | null;
  disabledHint?: string;
};

/**
 * Footer action row for the episode form. The primary submit button lives in
 * the page's sticky header; this row holds Cancel plus any submit feedback
 * (disabled hint, "already created" link, submit error). It is not a numbered
 * step.
 */
function SubmitActions({
  canSubmit,
  isSubmitting,
  submitError,
  onCancel,
  lockedEpisodeId = null,
  disabledHint = "Select an employee to continue.",
}: SubmitActionsProps) {
  const { t } = useTranslation();
  return (
    <section className="submitActions" aria-label={t("Form actions")}>
      <div className="submitActionsRow">
        <button
          className="secondaryButton"
          onClick={onCancel}
          type="button"
          disabled={isSubmitting}
        >
          {t("Cancel")}
        </button>
      </div>

      {!canSubmit ? (
        <p className="hintText sectionHint">{t(disabledHint)}</p>
      ) : null}

      {lockedEpisodeId ? (
        <p className="hintText sectionHint">
          {t(
            "Episode was already created. Open the episode to continue managing follow-ups.",
          )}{" "}
          <Link className="breadcrumbLink" to={`/episodes/${lockedEpisodeId}`}>
            {t("Open created episode")}
          </Link>
        </p>
      ) : null}

      {submitError ? (
        <p className="errorMessage" role="alert">
          {submitError}
        </p>
      ) : null}
    </section>
  );
}

export default SubmitActions;
